// src/components/DialogUtils.jsx - 순수 React Dialog 사용 버전
import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Info, CheckCircle, LogIn, Trash2, Upload } from 'lucide-react';

// 🔴 확인 대화상자 (confirm 대체)
export const ConfirmDialog = ({ 
  open, 
  onOpenChange, 
  title, 
  description, 
  onConfirm, 
  onCancel,
  confirmText = "확인",
  cancelText = "취소",
  variant = "default", // "default" | "destructive"
  icon
}) => {
  const getIcon = () => {
    if (icon) return icon;
    if (variant === "destructive") return <AlertTriangle className="w-6 h-6 text-red-600" />;
    return <Info className="w-6 h-6 text-blue-600" />;
  };

  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[425px] relative" 
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {getIcon()}
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription className="text-left mt-2 whitespace-pre-line">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 mt-6">
          <Button 
            variant="outline" 
            onClick={handleCancel}
          >
            {cancelText}
          </Button>
          <Button 
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 🔵 알림 대화상자 (alert 대체)
export const AlertDialog = ({ 
  open, 
  onOpenChange, 
  title, 
  description, 
  type = "info", // "info" | "success" | "warning" | "error"
  buttonText = "확인"
}) => {
  const getIconAndColor = () => {
    switch(type) {
      case "success":
        return { icon: <CheckCircle className="w-6 h-6 text-green-600" />, color: "text-green-600" };
      case "warning":
        return { icon: <AlertTriangle className="w-6 h-6 text-yellow-600" />, color: "text-yellow-600" };
      case "error":
        return { icon: <AlertTriangle className="w-6 h-6 text-red-600" />, color: "text-red-600" };
      default:
        return { icon: <Info className="w-6 h-6 text-blue-600" />, color: "text-blue-600" };
    }
  };

  const { icon, color } = getIconAndColor();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[425px] relative" 
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {icon}
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription className="text-left mt-2 whitespace-pre-line">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-6">
          <Button onClick={() => onOpenChange(false)}>
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 🔑 로그인 필요 대화상자
export const LoginRequiredDialog = ({ 
  open, 
  onOpenChange, 
  onLogin,
  action = "이 기능을 사용"
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[425px] relative" 
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <LogIn className="w-6 h-6 text-blue-600" />
            <span>로그인이 필요합니다</span>
          </DialogTitle>
          <DialogDescription className="text-left mt-2">
            {action}하려면 먼저 로그인해주세요.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 mt-6">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            취소
          </Button>
          <Button 
            onClick={() => {
              onLogin?.();
              onOpenChange(false);
            }}
          >
            로그인하기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 🗑️ 삭제 확인 대화상자
export const DeleteConfirmDialog = ({ 
  open, 
  onOpenChange, 
  title = "삭제 확인",
  itemName,
  onConfirm,
  onCancel
}) => {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={`"${itemName}"을(를) 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmText="삭제"
      cancelText="취소"
      variant="destructive"
      icon={<Trash2 className="w-6 h-6 text-red-600" />}
    />
  );
};

// 📤 업로드 확인 대화상자
export const UploadConfirmDialog = ({ 
  open, 
  onOpenChange, 
  fileName,
  fileSize,
  onConfirm,
  onCancel
}) => {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="파일 업로드 확인"
      description={`다음 파일을 업로드하시겠습니까?\n\n파일명: ${fileName}\n크기: ${fileSize}`}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmText="업로드"
      cancelText="취소"
      icon={<Upload className="w-6 h-6 text-blue-600" />}
    />
  );
};